/**
 * Calendar utility functions for generating calendar event URLs and ICS files
 * Used in onboarding to add practice reminder events to user calendars
 */

// Map of target language codes to event titles
const eventTitles = {
  fr: 'French Practice - Madame AI',
  en: 'English Practice - Madame AI',
  es: 'Spanish Practice - Madame AI',
  de: 'German Practice - Madame AI',
  it: 'Italian Practice - Madame AI',
  pt: 'Portuguese Practice - Madame AI',
  zh: 'Chinese Practice - Madame AI',
  ar: 'Arabic Practice - Madame AI',
  hi: 'Hindi Practice - Madame AI',
  vi: 'Vietnamese Practice - Madame AI',
  pl: 'Polish Practice - Madame AI',
  tr: 'Turkish Practice - Madame AI',
  ja: 'Japanese Practice - Madame AI',
  ko: 'Korean Practice - Madame AI',
  ru: 'Russian Practice - Madame AI',
};

// Day names for RRULE BYDAY parameter (ISO week: Monday = 0)
const rruleDays = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];

/**
 * Get the event title based on target language
 * @param {string} targetLanguageCode - Language code (e.g., 'fr', 'en')
 * @returns {string} Event title
 */
export function getEventTitle(targetLanguageCode) {
  return eventTitles[targetLanguageCode] || eventTitles.fr;
}

/**
 * Get the event description
 * @returns {string} Event description
 */
export function getEventDescription() {
  return "Time to practice your language skills! Click to start your session.";
}

/**
 * Get the app URL for the calendar event
 * @returns {string} App URL
 */
export function getAppUrl() {
  return 'https://conversaflex.com/madameai';
}

/**
 * Convert selected days array to RRULE BYDAY string
 * @param {boolean[]} days - Array of 7 booleans (Mon-Sun)
 * @returns {string} BYDAY string (e.g., "MO,TU,WE,TH,FR")
 */
function daysToRruleByday(days) {
  const selectedDays = days
    .map((selected, index) => selected ? rruleDays[index] : null)
    .filter(Boolean);
  return selectedDays.join(',');
}

/**
 * Parse time string to hours and minutes
 * @param {string} time - Time string (e.g., "09:00")
 * @returns {{hours: number, minutes: number}}
 */
function parseTime(time) {
  const [hours, minutes] = time.split(':').map(Number);
  return { hours, minutes };
}

/**
 * Format date for Google Calendar URL (YYYYMMDDTHHMMSS)
 * @param {Date} date - Date object
 * @returns {string} Formatted date string
 */
function formatGoogleDate(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}T${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
}

/**
 * Format date for ICS file (YYYYMMDDTHHMMSSZ)
 * @param {Date} date - Date object (will be converted to UTC)
 * @returns {string} Formatted UTC date string
 */
function formatIcsDate(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(date.getUTCSeconds())}Z`;
}

/**
 * Get the next occurrence of a selected day from today
 * @param {boolean[]} days - Array of 7 booleans (Mon-Sun)
 * @param {string} time - Time string (e.g., "09:00")
 * @returns {Date} Next occurrence date
 */
function getNextOccurrence(days, time) {
  const { hours, minutes } = parseTime(time);
  const now = new Date();
  const today = now.getDay(); // 0 = Sunday, 1 = Monday, etc.

  // Convert to ISO week (0 = Monday)
  const todayIso = today === 0 ? 6 : today - 1;

  // Find the next selected day
  for (let i = 0; i < 7; i++) {
    const checkDay = (todayIso + i) % 7;
    if (days[checkDay]) {
      const result = new Date(now);
      result.setDate(result.getDate() + i);
      result.setHours(hours, minutes, 0, 0);

      // If it's today but the time has passed, try the next occurrence
      if (i === 0 && result <= now) {
        continue;
      }
      return result;
    }
  }

  // Fallback: return tomorrow at the specified time
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(hours, minutes, 0, 0);
  return tomorrow;
}

/**
 * Generate Google Calendar URL with recurring event
 * @param {string} time - Time string (e.g., "09:00")
 * @param {boolean[]} days - Array of 7 booleans (Mon-Sun)
 * @param {string} targetLanguageCode - Language code for event title
 * @returns {string} Google Calendar URL
 */
export function generateGoogleCalendarUrl(time, days, targetLanguageCode) {
  const title = getEventTitle(targetLanguageCode);
  const description = getEventDescription();
  const location = getAppUrl();

  // Calculate start and end times (30 minute duration)
  const startDate = getNextOccurrence(days, time);
  const endDate = new Date(startDate.getTime() + 30 * 60 * 1000);

  // Build RRULE for weekly recurrence on selected days
  const byday = daysToRruleByday(days);
  const rrule = `RRULE:FREQ=WEEKLY;BYDAY=${byday}`;

  // Build URL parameters
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    details: description,
    location: location,
    dates: `${formatGoogleDate(startDate)}/${formatGoogleDate(endDate)}`,
    recur: rrule,
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Generate Outlook.com calendar URL
 * Note: Outlook web doesn't support RRULE in URL, so users need to set recurrence manually
 * @param {string} time - Time string (e.g., "09:00")
 * @param {boolean[]} days - Array of 7 booleans (Mon-Sun)
 * @param {string} targetLanguageCode - Language code for event title
 * @returns {string} Outlook calendar URL
 */
export function generateOutlookUrl(time, days, targetLanguageCode) {
  const title = getEventTitle(targetLanguageCode);
  const description = getEventDescription();
  const location = getAppUrl();

  // Calculate start and end times
  const startDate = getNextOccurrence(days, time);
  const endDate = new Date(startDate.getTime() + 30 * 60 * 1000);

  // Outlook uses ISO format
  const startIso = startDate.toISOString();
  const endIso = endDate.toISOString();

  // Build URL parameters
  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: title,
    body: description,
    location: location,
    startdt: startIso,
    enddt: endIso,
  });

  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}

/**
 * Generate ICS file content for download
 * @param {string} time - Time string (e.g., "09:00")
 * @param {boolean[]} days - Array of 7 booleans (Mon-Sun)
 * @param {string} targetLanguageCode - Language code for event title
 * @returns {string} ICS file content
 */
export function generateIcsContent(time, days, targetLanguageCode) {
  const title = getEventTitle(targetLanguageCode);
  const description = getEventDescription();
  const location = getAppUrl();

  // Calculate start and end times
  const startDate = getNextOccurrence(days, time);
  const endDate = new Date(startDate.getTime() + 30 * 60 * 1000);

  // Build RRULE
  const byday = daysToRruleByday(days);
  const rrule = `RRULE:FREQ=WEEKLY;BYDAY=${byday}`;

  // Generate unique ID
  const uid = `madameai-practice-${Date.now()}@conversaflex.com`;
  const dtstamp = formatIcsDate(new Date());

  // ICS content (RFC 5545)
  const icsLines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Madame AI//Practice Reminders//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${dtstamp}`,
    `DTSTART:${formatIcsDate(startDate)}`,
    `DTEND:${formatIcsDate(endDate)}`,
    rrule,
    `SUMMARY:${title}`,
    `DESCRIPTION:${description}`,
    `LOCATION:${location}`,
    `URL:${location}`,
    'STATUS:CONFIRMED',
    'TRANSP:OPAQUE',
    'BEGIN:VALARM',
    'ACTION:DISPLAY',
    `DESCRIPTION:${title}`,
    'TRIGGER:-PT10M',
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  ];

  return icsLines.join('\r\n');
}

/**
 * Download ICS file to user's device
 * @param {string} icsContent - ICS file content
 * @param {string} filename - Filename for download (default: 'practice-reminder.ics')
 */
export function downloadIcsFile(icsContent, filename = 'practice-reminder.ics') {
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();

  // Cleanup
  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 100);
}

/**
 * Check if any days are selected
 * @param {boolean[]} days - Array of 7 booleans (Mon-Sun)
 * @returns {boolean} True if at least one day is selected
 */
export function hasSelectedDays(days) {
  return days.some(Boolean);
}
